/**
 * Zone represents a network segment or security zone within a site or cloud environment.
 * 
 * Key Concepts:
 * - Zone: A logical or physical network segment (e.g., "Production Floor", "DMZ")
 * - Domain: Classification of the zone's purpose (IT, OT, Cloud, DMZ, etc.)
 * - Purdue Level: Industrial security hierarchy level (L0-L5) from IEC 62443
 * 
 * Example: A zone named "SCADA Network" would have domain="OT" and purdue_level="L3"
 */
export type Domain = 'IT' | 'OT' | 'Cloud' | 'DMZ' | 'External' | 'IoT' | 'Physical';
export type PurdueLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L3.5' | 'L4' | 'L5';
export type Criticality = 'H' | 'M' | 'L';

export interface CloudZoneMetadata {
  vpc_id?: string;
  vpc_name?: string;
  vnet_id?: string; // Azure
  availability_zones?: string[];
  resource_group?: string;
  subnet_ids?: string[];
  security_groups?: string[];
  network_acls?: string[];
}

export interface Zone {
  id: string;
  org_id: string;
  site_id?: string;
  cloud_service_id?: string;
  name: string;
  domain: Domain;
  purdue_level?: PurdueLevel;
  cidrs?: string[];
  criticality?: Criticality;
  description?: string;
  data_classification?: string;
  backup_frequency?: string;
  cloud_metadata?: CloudZoneMetadata;
  created_at: string;
  updated_at: string;
}

export type ZoneInsert = Omit<Zone, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
};

export type ZoneUpdate = Partial<
  Omit<Zone, 'id' | 'org_id' | 'created_at' | 'updated_at'>
>;

export const DOMAIN_COLORS: Record<Domain, { bg: string; border: string; text: string }> = {
  IT: { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-700' },
  OT: { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-700' },
  Cloud: { bg: 'bg-purple-100', border: 'border-purple-500', text: 'text-purple-700' },
  DMZ: { bg: 'bg-yellow-100', border: 'border-yellow-500', text: 'text-yellow-700' },
  External: { bg: 'bg-gray-100', border: 'border-gray-500', text: 'text-gray-700' },
  IoT: { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-700' },
  Physical: { bg: 'bg-slate-100', border: 'border-slate-500', text: 'text-slate-700' },
};

export const PURDUE_LEVELS: PurdueLevel[] = ['L0', 'L1', 'L2', 'L3', 'L3.5', 'L4', 'L5'];

export const CLOUD_PROVIDERS = ['AWS', 'Azure', 'GCP', 'Oracle', 'IBM', 'Other'] as const;

// Region mappings per provider
export const AWS_REGIONS = [
  { value: 'eu-north-1', label: 'EU North (Stockholm)' },
  { value: 'eu-west-1', label: 'EU West (Ireland)' },
  { value: 'eu-central-1', label: 'EU Central (Frankfurt)' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
];

export const AZURE_REGIONS = [
  { value: 'northeurope', label: 'North Europe (Ireland)' },
  { value: 'westeurope', label: 'West Europe (Netherlands)' },
  { value: 'swedencentral', label: 'Sweden Central' },
  { value: 'eastus', label: 'East US (Virginia)' },
  { value: 'westus2', label: 'West US 2 (Washington)' },
];

export const GCP_REGIONS = [
  { value: 'europe-north1', label: 'Finland' },
  { value: 'europe-west1', label: 'Belgium' },
  { value: 'europe-west4', label: 'Netherlands' },
  { value: 'us-central1', label: 'Iowa' },
  { value: 'us-east1', label: 'South Carolina' },
];
